import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, Button, CircularProgress, Collapse, Stack, TextField, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import useApi from 'hooks/useApi';
import { diApi } from '../api';
import type { PipelineSpec, RecordEvaluation, SourceEntity, SourceRecordRow } from '../types';
import { MONO, entityNoun, errorMessage } from './ConditionBuilder';

interface SampleRailProps {
  clientUid: string;
  pipelineUid: string;
  sourceUid: string;
  entity: SourceEntity;
  spec: PipelineSpec;
  recordId: string | null;
  onRecordChange: (recordId: string | null) => void;
}

type Tone = 'ok' | 'warn' | 'skip';

function verdictFor(ev: RecordEvaluation, one: string): { tone: Tone; head: string } {
  switch (ev.plan.kind) {
    case 'CREATE':
      return { tone: 'ok', head: 'Would create a transaction' };
    case 'ADOPT':
      return { tone: 'ok', head: 'Already tracked — would be adopted' };
    case 'FLAG':
      return { tone: 'warn', head: 'Already tracked — would be flagged' };
    case 'UPDATE':
      return { tone: 'ok', head: 'Would update the transaction' };
    case 'NOOP':
      return { tone: 'skip', head: 'No change — nothing this pipeline maps changed' };
    case 'CANCEL':
      return { tone: 'warn', head: 'Would cancel the transaction' };
    case 'HOLD':
      return { tone: 'warn', head: 'Would be held for review' };
    case 'SKIP':
    default:
      return { tone: 'skip', head: ev.matched ? `Would skip this ${one}` : 'Skipped by the filter' };
  }
}

/** Raw source data, if the preview carries it (not part of RecordEvaluation — read defensively). */
function rawFieldsOf(ev: RecordEvaluation | null): Record<string, unknown> | null {
  if (!ev) return null;
  const x = ev as unknown as { source?: unknown; data?: unknown; raw?: unknown; record?: { data?: unknown } };
  const raw = x.source ?? x.data ?? x.raw ?? x.record?.data;
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;
}

const show = (v: unknown) => (v == null || v === '' ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v));

export default function SampleRail({ clientUid, pipelineUid, sourceUid, entity, spec, recordId, onRecordChange }: SampleRailProps) {
  const theme = useTheme();
  const { showToast } = useApi();
  const toastRef = useRef(showToast);
  toastRef.current = showToast;
  const onRecordChangeRef = useRef(onRecordChange);
  onRecordChangeRef.current = onRecordChange;
  const recordIdRef = useRef(recordId);
  recordIdRef.current = recordId;

  const [one, many] = entityNoun(entity);
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<SourceRecordRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<SourceRecordRow | null>(null);
  const [loadingRows, setLoadingRows] = useState(false);
  const autoPicked = useRef(false);

  const [evaluation, setEvaluation] = useState<RecordEvaluation | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [rawOpen, setRawOpen] = useState(false);
  const seq = useRef(0);

  // Record picker: server-side search, debounced while typing.
  useEffect(() => {
    let alive = true;
    const timer = setTimeout(
      () => {
        setLoadingRows(true);
        diApi
          .getRecords(clientUid, sourceUid, { entity, search: search.trim() || undefined, limit: 25 })
          .then((r) => {
            if (!alive) return;
            const data = r.data || [];
            setRows(data);
            if (!autoPicked.current) {
              autoPicked.current = true;
              if (!recordIdRef.current && data.length) {
                setSelectedRow(data[0]);
                onRecordChangeRef.current(data[0].recordId);
              }
            }
          })
          .catch((e) => {
            if (alive) toastRef.current(errorMessage(e, `Could not load sample ${many}`), 'error');
          })
          .finally(() => {
            if (alive) setLoadingRows(false);
          });
      },
      search ? 300 : 0
    );
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [clientUid, sourceUid, entity, search, many]);

  // Preview the draft (unsaved edits included) against the chosen record, debounced on spec edits.
  useEffect(() => {
    if (!recordId) {
      setEvaluation(null);
      setPreviewing(false);
      return undefined;
    }
    const my = ++seq.current;
    setPreviewing(true);
    const timer = setTimeout(() => {
      diApi
        .preview(clientUid, pipelineUid, { recordId, spec })
        .then((r) => {
          if (seq.current !== my) return;
          setEvaluation(r.data);
          setPreviewError(null);
        })
        .catch((e) => {
          if (seq.current === my) setPreviewError(errorMessage(e, 'Preview failed'));
        })
        .finally(() => {
          if (seq.current === my) setPreviewing(false);
        });
    }, 500);
    return () => clearTimeout(timer);
  }, [clientUid, pipelineUid, recordId, spec]);

  const selected = useMemo<SourceRecordRow | null>(() => {
    if (!recordId) return null;
    return (
      rows.find((r) => r.recordId === recordId) ??
      (selectedRow?.recordId === recordId ? selectedRow : { recordId, action: 'update', receivedAt: '', summary: '' })
    );
  }, [recordId, rows, selectedRow]);

  const options = useMemo(
    () => (selected && !rows.some((r) => r.recordId === selected.recordId) ? [selected, ...rows] : rows),
    [rows, selected]
  );

  const ev = evaluation && evaluation.recordId === recordId ? evaluation : null;
  const raw = rawFieldsOf(ev);
  const sourceMapped = ev ? ev.mapped.filter((m) => m.owner === 'SOURCE') : [];
  const opsMapped = ev ? ev.mapped.filter((m) => m.owner === 'OPERATIONS').length : 0;
  const missing = sourceMapped.filter((m) => m.missing);
  const verdict = ev ? verdictFor(ev, one) : null;
  const toneBg: Record<Tone, string> = {
    ok: alpha(theme.palette.success.main, 0.1),
    warn: alpha(theme.palette.warning.main, 0.14),
    skip: theme.palette.grey[100]
  };
  const toneInk: Record<Tone, string> = {
    ok: theme.palette.success.dark,
    warn: theme.palette.warning.dark,
    skip: theme.palette.text.primary
  };

  return (
    <Box
      component="aside"
      aria-labelledby="di-rail-title"
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '10px',
        p: 2,
        position: 'sticky',
        top: 88,
        display: 'grid',
        gap: 1.75,
        gridTemplateColumns: 'minmax(0,1fr)',
        maxHeight: 'calc(100vh - 104px)',
        overflowY: 'auto'
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography id="di-rail-title" variant="h6" sx={{ fontSize: 15 }}>
          Sample {one}
        </Typography>
        {previewing && <CircularProgress size={14} />}
      </Stack>

      <Autocomplete
        size="small"
        options={options}
        value={selected}
        loading={loadingRows}
        filterOptions={(x) => x}
        getOptionLabel={(o) => o.recordId}
        isOptionEqualToValue={(a, b) => a.recordId === b.recordId}
        onChange={(_, v) => {
          setSelectedRow(v);
          onRecordChange(v?.recordId ?? null);
        }}
        onInputChange={(_, v, reason) => {
          if (reason === 'input') setSearch(v);
          else if (reason === 'clear') setSearch('');
        }}
        noOptionsText={loadingRows ? 'Loading…' : `No ${many} found`}
        renderOption={(props, o) => (
          <li {...props} key={o.recordId}>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ fontFamily: MONO, fontSize: 13 }}>
                {o.recordId}{' '}
                <Typography component="span" variant="caption" color="text.secondary">
                  · {o.action}
                </Typography>
              </Box>
              {o.summary && (
                <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ maxWidth: 260 }}>
                  {o.summary}
                </Typography>
              )}
            </Box>
          </li>
        )}
        renderInput={(p) => (
          <TextField
            {...p}
            placeholder={`Search ${many}`}
            inputProps={{ ...p.inputProps, 'aria-label': `Choose a sample ${one}`, style: { fontFamily: MONO, fontSize: 13 } }}
          />
        )}
      />
      {selected?.summary && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
          {selected.summary}
        </Typography>
      )}

      {!recordId && !loadingRows && (
        <Typography variant="body2" color="text.secondary">
          Pick a {one} to see what this pipeline would do with it.
        </Typography>
      )}

      {previewError && !previewing && (
        <Typography variant="body2" color="error.main">
          {previewError}
        </Typography>
      )}

      {recordId && !ev && previewing && (
        <Typography variant="body2" color="text.secondary">
          Checking {one} {recordId}…
        </Typography>
      )}

      {ev && verdict && (
        <Box sx={{ display: 'grid', gap: 1.75, opacity: previewing ? 0.6 : 1, transition: 'opacity .15s' }}>
          <Box
            sx={{
              borderRadius: '8px',
              px: 1.5,
              py: 1.25,
              fontSize: 13,
              display: 'grid',
              gap: 0.375,
              bgcolor: toneBg[verdict.tone],
              border: verdict.tone === 'skip' ? '1px solid' : 0,
              borderColor: 'divider'
            }}
          >
            <Typography component="strong" sx={{ fontWeight: 600, fontSize: 13, color: toneInk[verdict.tone] }}>
              {verdict.head}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 13 }}>
              {ev.plan.reason}
              {ev.plan.existingKey && (
                <>
                  {' '}
                  (
                  <Box component="code" sx={{ fontFamily: MONO, fontSize: 12 }}>
                    {ev.plan.existingKey}
                  </Box>
                  {ev.plan.existingTeamName ? ` in ${ev.plan.existingTeamName}` : ''})
                </>
              )}
            </Typography>
          </Box>

          <Stage name="Filter">
            {ev.filterTrace.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No conditions — every {one} matches.
              </Typography>
            ) : (
              <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.75 }}>
                {ev.filterTrace.map((c, i) => (
                  <Box component="li" key={i} sx={{ display: 'flex', gap: 0.875, fontSize: 12.5, lineHeight: 1.4 }}>
                    <Box
                      component="span"
                      aria-hidden
                      sx={{ width: 14, flex: 'none', fontWeight: 700, textAlign: 'center', color: c.pass ? 'success.main' : 'error.main' }}
                    >
                      {c.pass ? '✓' : '✕'}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                        {c.pass ? 'Passes: ' : 'Fails: '}
                      </Box>
                      {c.label}
                      {c.value && (
                        <Box
                          component="code"
                          sx={{ display: 'block', fontFamily: MONO, fontSize: 11.5, color: 'text.secondary', wordBreak: 'break-all' }}
                        >
                          {c.value}
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Stage>

          <Stage name="Target">
            <Typography variant="body2">
              {ev.target?.teamName ?? (
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  —
                </Box>
              )}
            </Typography>
          </Stage>

          <Stage name="Mapping">
            {!ev.matched ? (
              <Typography variant="body2" color="text.secondary">
                Not mapped
              </Typography>
            ) : (
              <>
                <Typography variant="body2">
                  {sourceMapped.length - missing.length} of {sourceMapped.length} Aljex field{sourceMapped.length === 1 ? '' : 's'} filled
                  {opsMapped ? ` · ${opsMapped} left to Operations` : ''}
                </Typography>
                {missing.length > 0 && (
                  <Typography variant="body2" sx={{ color: 'warning.dark' }}>
                    Missing: {missing.map((m) => m.label).join(', ')}
                  </Typography>
                )}
              </>
            )}
          </Stage>

          <Stage name="Plan">
            <Typography variant="body2">
              {ev.plan.kind === 'SKIP' && !ev.matched ? (
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  Nothing — the {one} is skipped
                </Box>
              ) : (
                ev.plan.reason || ev.plan.kind
              )}
            </Typography>
          </Stage>

          {ev.warnings?.length > 0 && (
            <Stage name="Warnings">
              {ev.warnings.map((w, i) => (
                <Typography key={i} variant="body2" sx={{ color: 'warning.dark', fontSize: 12.5 }}>
                  {w}
                </Typography>
              ))}
            </Stage>
          )}

          <Box>
            <Button
              size="small"
              onClick={() => setRawOpen((o) => !o)}
              aria-expanded={rawOpen}
              sx={{ px: 0, minWidth: 0, textTransform: 'none', fontWeight: 500 }}
            >
              {rawOpen ? '▾' : '▸'} {raw ? `Source fields for this ${one}` : `Mapped values for this ${one}`}
            </Button>
            <Collapse in={rawOpen} unmountOnExit>
              {raw ? (
                <KeyValues
                  rows={Object.keys(raw)
                    .sort()
                    .filter((k) => raw[k] != null && raw[k] !== '')
                    .map((k) => [k, show(raw[k])])}
                />
              ) : ev.mapped.length ? (
                <KeyValues rows={ev.mapped.map((m) => [m.label, m.missing ? '— missing' : m.display || show(m.value)])} />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Nothing mapped for this {one}.
                </Typography>
              )}
            </Collapse>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function Stage({ name, children }: { name: string; children: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gap: 0.625, fontSize: 13 }}>
      <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.07em', color: 'text.secondary', fontWeight: 600 }}>
        {name}
      </Typography>
      {children}
    </Box>
  );
}

function KeyValues({ rows }: { rows: Array<[string, string]> }) {
  return (
    <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'auto minmax(0,1fr)', gap: '4px 10px', m: '10px 0 0', fontSize: 12 }}>
      {rows.map(([k, v]) => (
        <Box key={k} sx={{ display: 'contents' }}>
          <Box component="dt" sx={{ fontFamily: MONO, color: 'text.secondary' }}>
            {k}
          </Box>
          <Box component="dd" sx={{ m: 0, fontFamily: MONO, wordBreak: 'break-all' }}>
            {v}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
